// Peças compartilhadas pelos dois modais do botão "Mover" de uma categoria:
// "Mover para outra forma de cobrança" e "Transferir para outro fornecedor".
//
// Os dois seguem o mesmo padrão visual: um "herói" (ícone circular + frase
// centralizada) no lugar do título, bloco de contexto com os valores em
// destaque, o conteúdo do passo e o rodapé Cancelar/ação.

import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { IoMdClose } from "react-icons/io";
import { FiCheck } from "react-icons/fi";
import { MdInfoOutline, MdWarningAmber } from "react-icons/md";
import { CalcOption } from "../../../../types/budget/budget";

export const COLORS = {
  accent: "#1D4ED8",
  accentDark: "#1A43B8",
  accentSoft: "#EFF4FF",
  accentBorder: "#C7D9FB",
  surface: "#F4F7FE",
  surfaceBorder: "#E2E9F7",
  warnBg: "#FFF8EF",
  warnBorder: "#F7DCB8",
  warnIcon: "#E08A2E",
  success: "#1E9E4A",
  text: "#1F2937",
  muted: "#6B7280",
  cardBorder: "#E1E5EC",
};

/** Rótulo curto da forma de cobrança, para cabeçalhos e frases corridas. */
export const CALC_OPTION_SHORT_LABEL: Record<CalcOption, string> = {
  A: "A - Pagamento Direto",
  B: "B - Nota Débito",
  C: "C - Custos de Terceiros",
  D: "D - Custos Internos",
};

/**
 * Barra superior só com o "fechar".
 *
 * Os modais não têm título escrito: quem diz o que a tela faz é o `ModalHero`
 * logo abaixo. O `aria-label` do `Dialog` continua nomeando a janela para
 * leitores de tela.
 */
export const ModalCloseBar: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      px: 1.5,
      pt: 1.5,
      pb: 0,
    }}
  >
    <IconButton size="small" onClick={onClose} aria-label="Fechar">
      <IoMdClose />
    </IconButton>
  </Box>
);

/** Ícone circular + frase centralizada no topo do passo. */
export const ModalHero: React.FC<{
  icon: React.ReactNode;
  tone?: "info" | "warning";
  children: React.ReactNode;
}> = ({ icon, tone = "info", children }) => (
  <Box sx={{ textAlign: "center", mb: 2.5 }}>
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "50%",
        backgroundColor: tone === "warning" ? "#FDEEDC" : "#E8F0FE",
        color: tone === "warning" ? COLORS.warnIcon : COLORS.accent,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1.5,
      }}
    >
      {icon}
    </Box>
    <Typography
      component="div"
      sx={{ fontSize: 13.5, color: COLORS.muted, lineHeight: 1.6, px: 2 }}
    >
      {children}
    </Typography>
  </Box>
);

export const WarningHero: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ModalHero icon={<MdWarningAmber size={26} />} tone="warning">
    {children}
  </ModalHero>
);

/** Termo em destaque dentro das frases (categoria, fornecedor, forma). */
export const Highlight: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    component="span"
    sx={{ color: COLORS.accent, fontWeight: 700, whiteSpace: "nowrap" }}
  >
    {children}
  </Box>
);

export const SummaryRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: "flex", gap: 1.5, py: 0.45, flexWrap: "wrap" }}>
    <Typography sx={{ fontSize: 13, color: COLORS.muted, minWidth: 128 }}>
      {label}:
    </Typography>
    <Typography
      sx={{ fontSize: 13, fontWeight: 700, color: COLORS.accent, flex: 1 }}
    >
      {value}
    </Typography>
  </Box>
);

export const SummaryBlock: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.surfaceBorder}`,
      borderRadius: "8px",
      px: 2,
      py: 1.4,
      mb: 2.5,
    }}
  >
    {children}
  </Box>
);

/** Rótulo de seção com dica opcional (o "ⓘ" do protótipo). */
export const SectionLabel: React.FC<{ text: string; hint?: string }> = ({
  text,
  hint,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
      {text}
    </Typography>
    {hint && (
      <Tooltip title={hint}>
        <Box sx={{ display: "inline-flex", color: COLORS.muted }}>
          <MdInfoOutline size={15} />
        </Box>
      </Tooltip>
    )}
  </Box>
);

/** Opção em formato de cartão selecionável (substitui o radio "pelado"). */
export const ChoiceCard: React.FC<{
  selected: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  onSelect: () => void;
}> = ({ selected, disabled = false, icon, title, description, onSelect }) => (
  <Box
    role="radio"
    aria-checked={selected}
    aria-disabled={disabled}
    tabIndex={disabled ? -1 : 0}
    onClick={() => !disabled && onSelect()}
    onKeyDown={(event) => {
      if (disabled) return;
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        onSelect();
      }
    }}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25,
      border: `1px solid ${selected ? COLORS.accent : COLORS.cardBorder}`,
      backgroundColor: selected ? COLORS.accentSoft : "#FFF",
      borderRadius: "8px",
      px: 1.5,
      py: 1.4,
      mb: 1.25,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      transition: "border-color .15s, background-color .15s",
      "&:hover": disabled
        ? undefined
        : { borderColor: selected ? COLORS.accent : "#B9C3D4" },
    }}
  >
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: `2px solid ${selected ? COLORS.accent : "#B0B8C6"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: "3px",
      }}
    >
      {selected && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: COLORS.accent,
          }}
        />
      )}
    </Box>

    {icon && (
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "6px",
          backgroundColor: selected ? "#DDE8FD" : "#F2F4F7",
          color: selected ? COLORS.accent : COLORS.muted,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    )}

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 700,
          color: selected ? COLORS.accent : COLORS.text,
          mb: 0.25,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: COLORS.muted, lineHeight: 1.5 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

/** Caixa âmbar com o aviso específico do passo de confirmação. */
export const WarningBox: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      backgroundColor: COLORS.warnBg,
      border: `1px solid ${COLORS.warnBorder}`,
      borderRadius: "8px",
      px: 2,
      py: 1.4,
      mb: 2,
    }}
  >
    <Typography sx={{ fontSize: 13, color: COLORS.text, lineHeight: 1.65 }}>
      {children}
    </Typography>
  </Box>
);

export const InfoBox: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      backgroundColor: COLORS.accentSoft,
      border: `1px solid ${COLORS.accentBorder}`,
      borderRadius: "8px",
      px: 2,
      py: 1.4,
      mb: 2,
    }}
  >
    <Typography sx={{ fontSize: 13, color: COLORS.text, lineHeight: 1.65 }}>
      {children}
    </Typography>
  </Box>
);

/** Lista de consequências da ação, com check verde (sem moldura). */
export const ConsequenceList: React.FC<{ items: React.ReactNode[] }> = ({
  items,
}) => (
  <Box>
    {items.map((item, index) => (
      <Box
        key={index}
        sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", py: 0.6 }}
      >
        <Box
          sx={{
            width: 17,
            height: 17,
            borderRadius: "50%",
            backgroundColor: "#E6F5EC",
            color: COLORS.success,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: "2px",
          }}
        >
          <FiCheck size={11} strokeWidth={3} />
        </Box>
        <Typography sx={{ fontSize: 13, color: COLORS.text, lineHeight: 1.55 }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const SuccessStep: React.FC<{
  message: React.ReactNode;
  detail?: string;
}> = ({ message, detail }) => (
  <Box sx={{ textAlign: "center", py: 2 }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        backgroundColor: "#E6F5EC",
        color: COLORS.success,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      <FiCheck size={28} strokeWidth={3} />
    </Box>
    <Typography
      component="div"
      sx={{ fontSize: 14, color: COLORS.text, mb: 1, lineHeight: 1.6, px: 2 }}
    >
      {message}
    </Typography>
    {detail && (
      <Typography sx={{ fontSize: 12.5, color: COLORS.muted, px: 2 }}>
        {detail}
      </Typography>
    )}
  </Box>
);

/** Rodapé com os botões centralizados (padrão dos dois modais). */
export const modalActionsSx = {
  px: 3,
  py: 2,
  gap: 1.5,
  justifyContent: "center",
} as const;

/** Estilos do rodapé: "Cancelar" contornado + ação primária sólida. */
export const cancelButtonSx = {
  textTransform: "none",
  fontSize: 14,
  fontWeight: 600,
  color: COLORS.text,
  borderColor: COLORS.cardBorder,
  borderRadius: "6px",
  px: 3,
  py: 0.9,
  "&:hover": { borderColor: "#B9C3D4", backgroundColor: "#F7F8FA" },
} as const;

export const primaryButtonSx = {
  textTransform: "none",
  fontSize: 14,
  fontWeight: 600,
  backgroundColor: COLORS.accent,
  borderRadius: "6px",
  px: 3,
  py: 0.9,
  boxShadow: "none",
  "&:hover": { backgroundColor: COLORS.accentDark, boxShadow: "none" },
} as const;
